import React from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Img from 'gatsby-image';
import { CareerValuesPageQuery } from '../../../graphql-types';

import Layout from '../../components/layout';
import SEO from '../../components/seo';

import { BREAKPOINTS, COLORS } from '../../ui/variables';
import Button from '../../ui/BaseButton';
import Heading1 from '../../ui/Heading1';
import Heading2 from '../../ui/Heading2';
import Heading3 from '../../ui/Heading3';
import ParagraphLarge from '../../ui/ParagraphLarge';
import CareersNavigation from '../../components/careers/navigation';
import CareerTestimonials from '../../components/careers/testimonials';
import OpenPositions from '../../components/careers/open-positions';
import { TwoUp, TwoUpChild } from './index';

const Hero = styled.div`
  background: ${COLORS.LIGHT_BEIGE};
  padding: 100px 0;
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1026px;
  margin: auto;
`;

const HeroCTAWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${COLORS.LIGHT_BEIGE};
  background: linear-gradient(
    180deg,
    rgba(253, 250, 244, 1) 0%,
    rgba(253, 250, 244, 1) 85%,
    rgba(253, 250, 244, 0) 100%
  );
  position: sticky;
  top: 140px;
  height: 400px;
  z-index: 2;

  @media ${BREAKPOINTS.TABLET} {
    flex-direction: column;
    text-align: center;
    position: inherit;
  }
`;

const HeroCTA = styled(Button)`
  padding: 18px 32px;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 14px 24px;
  }
`;

const HeroValueWrapper = styled.div`
  max-width: 900px;
  margin: auto;
  margin-top: 120px;
  height: 400px;

  @media ${BREAKPOINTS.TABLET} {
    flex-direction: column;
    padding: 0 20px;
    height: 600px;
  }
`;

const HeroValue = styled.div`
  display: flex;
  position: sticky;
  top: 600px;
  height: max-content;

  @media ${BREAKPOINTS.TABLET} {
    top: 170px;
    flex-direction: column;
  }

  > h1 {
    color: ${COLORS.ORANGE};
    margin: 0 54px 0 0;
    font-size: 65px;

    @media ${BREAKPOINTS.TABLET} {
      margin-bottom: 200px;
    }
  }

  > h3 {
    max-width: 810px;
    margin: 0;
    line-height: 42px;
    font-weight: 400;
  }
`;

const Why = styled.div`
  background: ${COLORS.LIGHT_BEIGE};
`;

const WhyContent = styled.div`
  margin: auto;
  padding: 200px 0 140px 0;
  max-width: 920px;
  width: 100%;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 80px 0;
  }
`;

const WhyReasons = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  list-style: none;
  margin: 0;

  @media ${BREAKPOINTS.MOBILE} {
    justify-content: center;
  }
`;

const Reason = styled.li`
  flex-shrink: 0;
  max-width: 378px;
  width: 100%;
  margin-bottom: 80px;

  &:last-child {
    margin-bottom: 0;
  }

  @media ${BREAKPOINTS.MOBILE} {
    max-width: 272px;
    font-size: 18px;
    color: ${COLORS.BLACK};
  }
`;

const ReasonIcon = styled(Img)`
  margin-bottom: 24px;
`;

const Safety = styled.div`
  width: 100%;
  max-width: 750px;
  text-align: center;
  margin: auto;
  padding: 140px 0;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 80px 20px;
  }
`;

export const pageQuery = graphql`
  query CareerValuesPage {
    womanPettingDog: file(
      relativePath: { eq: "pages/careers/woman-petting-dog.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1440) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    womanFeedingPuppy: file(
      relativePath: { eq: "pages/careers/woman-feeding-puppy.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 486) {
          ...GatsbyImageSharpFluid_withWebp
          ...GatsbyImageSharpFluidLimitPresentationSize
        }
      }
    }
    publicBenefit: file(
      relativePath: { eq: "pages/careers/public-benefit-corp-logo.png" }
    ) {
      childImageSharp {
        fixed(width: 108, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    bCorp: file(relativePath: { eq: "pages/careers/b-corp-logo.png" }) {
      childImageSharp {
        fixed(width: 108, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    welcomeGraphic: file(
      relativePath: { eq: "pages/careers/welcome-graphic.png" }
    ) {
      childImageSharp {
        fixed(width: 326, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    moneyBagIcon: file(
      relativePath: { eq: "pages/careers/icons/money-bag.png" }
    ) {
      childImageSharp {
        fixed(height: 48, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    clockIcon: file(relativePath: { eq: "pages/careers/icons/clock.png" }) {
      childImageSharp {
        fixed(height: 48, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    bookIcon: file(relativePath: { eq: "pages/careers/icons/book.png" }) {
      childImageSharp {
        fixed(height: 48, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    sunIcon: file(relativePath: { eq: "pages/careers/icons/sun.png" }) {
      childImageSharp {
        fixed(height: 48, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    heartIcon: file(relativePath: { eq: "pages/careers/icons/heart.png" }) {
      childImageSharp {
        fixed(height: 48, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    microscopeIcon: file(
      relativePath: { eq: "pages/careers/icons/microscope.png" }
    ) {
      childImageSharp {
        fixed(height: 48, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
  }
`;

interface Props {
  data: CareerValuesPageQuery;
}

const CareerValuesPage = ({ data }: Props) => {
  return (
    <Layout>
      <SEO
        title='Our Values &amp; Careers'
        description="Our values are helping us reimagine veterinary care in New York. Small Door's values can be seen in our culture of diversity, growth, &amp; work-life balance."
      />
      <CareersNavigation />

      <Hero>
        <HeroContent>
          <HeroCTAWrap>
            <div>
              <Heading1>Our Values</Heading1>
              <HeroCTA
                color={COLORS.ORANGE}
                type='external'
                href='https://boards.greenhouse.io/smalldoor'
              >
                See our open positions
              </HeroCTA>
            </div>
            <Img
              css={css`
                @media ${BREAKPOINTS.MOBILE} {
                  order: -1;
                  margin-bottom: 60px;
                }
              `}
              fixed={data.welcomeGraphic.childImageSharp.fixed}
            />
          </HeroCTAWrap>
          <HeroValueWrapper>
            <HeroValue>
              <Heading1>1</Heading1>
              <Heading3>
                We hold ourselves to high standards that strive to deliver
                positive impact in everything we do
              </Heading3>
            </HeroValue>
          </HeroValueWrapper>

          <HeroValueWrapper>
            <HeroValue>
              <Heading1>2</Heading1>
              <Heading3>
                We work collaboratively to build stronger teams and produce
                better outcomes
              </Heading3>
            </HeroValue>
          </HeroValueWrapper>

          <HeroValueWrapper>
            <HeroValue>
              <Heading1>3</Heading1>
              <Heading3>We believe in transparency</Heading3>
            </HeroValue>
          </HeroValueWrapper>

          <HeroValueWrapper>
            <HeroValue>
              <Heading1>4</Heading1>
              <Heading3>We treat everyone with respect and empathy</Heading3>
            </HeroValue>
          </HeroValueWrapper>

          <HeroValueWrapper
            css={css`
              padding-bottom: 100px;
            `}
          >
            <HeroValue>
              <Heading1>5</Heading1>
              <Heading3>
                We celebrate our successes and own our mistakes
              </Heading3>
            </HeroValue>
          </HeroValueWrapper>
        </HeroContent>
      </Hero>

      <Img
        css={css`
          margin-bottom: 160px;
        `}
        fluid={data.womanPettingDog.childImageSharp.fluid}
      />

      <TwoUp>
        <TwoUpChild
          css={css`
            width: 100%;
          `}
        >
          <Heading3>Diversity, Equity &amp; Inclusion</Heading3>
          <ParagraphLarge>
            We believe Diversity, Equity, Inclusion, and Belonging are core to
            our mission of reimagining the veterinary experience. Our team's
            diversity is key – spanning races, genders, ages, religions,
            identities, and backgrounds. We commit to fostering a welcoming,
            respectful, and empowering workplace, harnessing varied viewpoints
            for veterinary industry innovation. Our goal at Small Door is to
            create an inclusive culture, mirroring our communities, and offering
            optimal support for every employee's success. We’re excited to
            continue to challenge ourselves to diversify our workforce to drive
            innovation and deliver exceptional veterinary care.
          </ParagraphLarge>
        </TwoUpChild>
      </TwoUp>

      <TwoUp>
        <TwoUpChild
          css={css`
            @media ${BREAKPOINTS.TABLET} {
              margin-bottom: 60px;
            }
          `}
        >
          <Img fluid={data.womanFeedingPuppy.childImageSharp.fluid} />
        </TwoUpChild>
        <TwoUpChild>
          <Heading3>Women in veterinary</Heading3>
          <ParagraphLarge>
            As a profession that skews heavily female, we believe it's important
            for veterinary employers to be supportive of the women who power it.
            That's why we offer generous paid parental leave, subsidized family
            planning and fertility care, and flexible scheduling whereby we
            limit weekend workdays.
          </ParagraphLarge>
        </TwoUpChild>
      </TwoUp>

      <TwoUp
        css={css`
          align-items: flex-start;
        `}
      >
        <TwoUpChild
          css={css`
            @media ${BREAKPOINTS.TABLET} {
              margin-bottom: 60px;
            }
          `}
        >
          <Img
            css={css`
              margin: 0px 0;
            `}
            fixed={data.publicBenefit.childImageSharp.fixed}
          />
          <Heading3>Public Benefit Corporation</Heading3>
          <ParagraphLarge>
            Being a Public Benefit Corporation means that when we make business
            decisions, it's not only about profit. We also consider how to
            improve the working environment for other stakeholders - like our
            veterinarians, medical staff, employees and the pets we serve - in
            concrete, measurable ways.
          </ParagraphLarge>
        </TwoUpChild>
        <TwoUpChild>
          <Img fixed={data.bCorp.childImageSharp.fixed} />
          <Heading3>Certified B Corp</Heading3>
          <ParagraphLarge>
            As a Certified B Corporation, we're doing our part to do some good
            in the world. We are held to rigorous standards of social and
            environmental performance, public transparency, and accountability.
          </ParagraphLarge>
        </TwoUpChild>
      </TwoUp>

      <Why>
        <WhyContent>
          <Heading2
            css={css`
              text-align: center;
              margin-bottom: 100px;
            `}
          >
            Why Small Door?
          </Heading2>
          <WhyReasons>
            <Reason>
              <ReasonIcon fixed={data.moneyBagIcon.childImageSharp.fixed} />
              <br />
              Competitive salaries and comprehensive benefits, including a
              401(K) match.
            </Reason>
            <Reason>
              <ReasonIcon fixed={data.clockIcon.childImageSharp.fixed} />
              <br />
              Generous paid time off.
            </Reason>
            <Reason>
              <ReasonIcon fixed={data.bookIcon.childImageSharp.fixed} />
              <br />
              Separate time off and budget allocation for Continuing Education,
              an environment of mentorship and continuous learning, as well as
              opportunities for career growth and advancement.
            </Reason>
            <Reason>
              <ReasonIcon fixed={data.heartIcon.childImageSharp.fixed} />
              <br />
              Deeply discounted pet care at Small Door to help you take care of
              your furry family members.
            </Reason>
            <Reason>
              <ReasonIcon fixed={data.microscopeIcon.childImageSharp.fixed} />
              <br />A state-of-the-art hospital with plenty of natural light and
              technology that makes your life easier.
            </Reason>
            <Reason>
              <ReasonIcon fixed={data.sunIcon.childImageSharp.fixed} />
              <br />A positive and collaborative work environment and true
              work-life balance.
            </Reason>
          </WhyReasons>
        </WhyContent>
      </Why>

      <CareerTestimonials />
      <OpenPositions
        positions={[
          {
            name: 'Veterinarians',
            linkTo: '/careers/veterinarians',
            linkCopy: 'Learn more',
          },
          {
            name: 'Nurses',
            linkTo: '/careers/nurses',
            linkCopy: 'Learn more',
          },
          {
            name: 'Headquarters',
            linkTo: '/careers/headquarters',
            linkCopy: 'Learn more',
          },
        ]}
      />
    </Layout>
  );
};

export default CareerValuesPage;
